import React, { useEffect, useState } from "react";
import DataGrid, { Column, Scrolling } from "devextreme-react/data-grid";
import DateRangeBox from "devextreme-react/date-range-box";
import "./PnLCorrelations.css";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";

interface DateRange {
  start: string;
  end: string;
}

interface RangeHeaderProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const PnLCorrelations: React.FC = () => {
  const heatCell = ({ value, data, column }: any) => {
    const field = column?.dataField;
    const raw =
      value !== undefined ? value : field && data ? data[field] : undefined;

    const m = raw != null ? String(raw).match(/-?\d+(\.\d+)?/) : null;
    const p = m ? parseFloat(m[0]) : null; // percent number, e.g. "18%" -> 18
    if (p != null && Math.abs(p - 100) < 1e-9) return "100%";
    const colorFromPct = (p: number | null): string => {
      if (p == null || Number.isNaN(p)) return "transparent";
      if (Math.abs(p - 100) < 1e-9) return "#fff";

      const white = [255, 255, 255];
      const red = [255, 77, 79];
      const green = [82, 196, 26];

      const mix = (a: number, b: number, t: number) => a + (b - a) * t;
      const mixRGB = (a: number[], b: number[], t: number) =>
        `rgb(${Math.round(mix(a[0], b[0], t))}, ${Math.round(
          mix(a[1], b[1], t)
        )}, ${Math.round(mix(a[2], b[2], t))})`;

      if (p >= 70) return `rgb(${red[0]}, ${red[1]}, ${red[2]})`; // full red
      if (p > 0) return mixRGB(white, red, p / 70); // 0..70 -> shade to red
      if (p <= -50) return `rgb(${green[0]}, ${green[1]}, ${green[2]})`; // full green
      if (p < 0) return mixRGB(white, green, Math.abs(p) / 50); // -50..0 -> shade to green
      return "#fff"; // exactly 0
    };

    const bg = colorFromPct(p);

    return (
      <div
        style={{
          background: bg,
          borderRadius: 6,
          padding: "4px 6px",
          textAlign: "center",
          fontWeight: 600,
          color: "#111",
          whiteSpace: "nowrap",
        }}
        title={raw ?? ""}
      >
        {raw ?? ""}
      </div>
    );
  };
const fyRangeForDate = (d: Date): DateRange => {
  const y = d.getFullYear();
  const m = d.getMonth() + 1; // 1-12
  const startYear = m >= 4 ? y : y - 1;
  const endYear = startYear + 1;
  return {
    start: `${startYear}-04-01`,
    end: `${endYear}-03-31`,
  };
};

  const [cobDate, , , latestCobDate] = useCobDate("/pnl_latest_cob");

  const [marketData, setMarketData] = useState<Record<string, any>[]>([]);
  const [traderData, setTraderData] = useState<Record<string, any>[]>([]);
  const [marketCols, setMarketCols] = useState<string[]>([]);
  const [traderCols, setTraderCols] = useState<string[]>([]);

  const [rangeMarket, setRangeMarket] = useState<DateRange | null>(null);
  const [rangeTrader, setRangeTrader] = useState<DateRange | null>(null);

  // Once the latest COB is known, set the default ranges to its FY
  useEffect(() => {
    if (cobDate) {
      const range = fyRangeForDate(cobDate);
      setRangeMarket((prev) => prev ?? range);
      setRangeTrader((prev) => prev ?? range);
    }
  }, [cobDate]);

  useEffect(() => {
    if (!rangeMarket) return;
    const url = `${baseUrl}/pnl_market_corr?start_date=${rangeMarket.start}&end_date=${rangeMarket.end}`;

    fetch(url)
      .then((res) => res.json())
      .then(({ columns, data }: { columns: string[]; data: Record<string, any>[] }) => {
        setMarketCols(columns);
        setMarketData(data);
      })
      .catch((err) => console.error("Failed to load market corr data", err));
  }, [rangeMarket, baseUrl]);

  useEffect(() => {
    if (!rangeTrader) return;
    const url = `${baseUrl}/pnl_trader_corr?start_date=${rangeTrader.start}&end_date=${rangeTrader.end}`;

    fetch(url)
      .then((res) => res.json())
      .then(({ columns, data }: { columns: string[]; data: Record<string, any>[] }) => {
        setTraderCols(columns || []);
        setTraderData(data || []);
      })
      .catch((err) => console.error("Failed to load market corr data", err));
  }, [rangeTrader, baseUrl]);


  const RangeHeader: React.FC<RangeHeaderProps> = ({ value, onChange }) => (
    <DateRangeBox
       elementAttr={{ class: "range-sm" }}
      value={[value?.start, value?.end]}
      startDateLabel="Start"
      endDateLabel="End"
      stylingMode="outlined"
      width={250}
      height={36}
      displayFormat="dd/MM/yyyy"
      max={latestCobDate ?? undefined}
      onValueChanged={(e: any) => {
        const [s, t] = e.value || [];
        onChange({
          start: s ? s : "",
          end: t ? t : "",
        });
      }}
    />
  );
  const makeRowHeaderCell =
    (labels: string[]) =>
    ({ rowType, rowIndex }: { rowType: string; rowIndex: number }) => {
      if (rowType !== "data") return null; // ignore group/headers
      const label = labels[rowIndex] ?? "";
      return (
        <div style={{ fontWeight: 600, whiteSpace: "nowrap" ,fontSize:12 }}>{label}</div>
      );
    };
  if (!rangeMarket || !rangeTrader) return null;

  return (
    <div className="correlation-container">
      <div className="grid-wrapper-correlation">
        <DataGrid
          dataSource={marketData}
          showBorders={true}
          columnAutoWidth={true}
          wordWrapEnabled={true}
          rowAlternationEnabled={false}
          hoverStateEnabled={true}
           width="100%"
        height="100%"
        >
          <Scrolling columnRenderingMode="virtual" />

          <Column
            dataField="id"
            caption=""
            width={300}
            fixed
            allowSorting={false}
            allowFiltering={false}
            headerCellRender={() => (
              <RangeHeader value={rangeMarket} onChange={setRangeMarket} />
            )}
            cellRender={makeRowHeaderCell(marketCols)}
          />

          {marketCols.map((col) => (
            <Column
              key={col}
              dataField={col}
              caption={col}
              alignment="center"
              cellRender={heatCell}
            />
          ))}
        </DataGrid>
      </div>

      <div className="grid-wrapper">
        <DataGrid
          dataSource={traderData}
          showBorders={true}
          columnAutoWidth={true}
          wordWrapEnabled={true}
          rowAlternationEnabled={false}
          hoverStateEnabled={true}
        >
          <Scrolling columnRenderingMode="virtual" />
          <Column
            dataField="id"
            caption=""
            width={300}
            fixed
            allowSorting={false}
            allowFiltering={false}
            headerCellRender={() => (
              <RangeHeader value={rangeTrader} onChange={setRangeTrader} />
            )}
            cellRender={makeRowHeaderCell(traderCols)}
          />

          {traderCols.map((col) => (
            <Column
              key={col}
              dataField={col}
              caption={col}
              alignment="center"
              cellRender={heatCell}
            />
          ))}
        </DataGrid>
      </div>
    </div>
  );
};

export default PnLCorrelations;
